import { html, nothing } from "lit";
import type { LocalResourcesHistoryPoint, LocalResourcesSnapshot } from "../types.ts";

type LocalLlmBottleneckStripProps = {
  snapshot: LocalResourcesSnapshot | null;
  history: LocalResourcesHistoryPoint[];
};

function renderSparkline(
  history: LocalResourcesHistoryPoint[],
  field: "cpuPercent" | "memUsedPercent",
) {
  if (history.length < 2) {
    return nothing;
  }
  const width = 92;
  const height = 22;
  const values = history.map((point) => point[field]);
  const max = Math.max(1, ...values);
  const step = width / Math.max(1, values.length - 1);
  const points = values
    .map((v, i) => {
      const x = i * step;
      const y = height - (v / max) * height;
      return `${x},${y}`;
    })
    .join(" ");
  return html`
    <svg
      class="local-llm-bottleneck__sparkline"
      width=${width}
      height=${height}
      viewBox="0 0 ${width} ${height}"
      aria-hidden="true"
    >
      <polyline fill="none" stroke="currentColor" stroke-width="2" points=${points} />
    </svg>
  `;
}

export function renderLocalLlmBottleneckStrip(props: LocalLlmBottleneckStripProps) {
  const snap = props.snapshot;
  if (!snap) {
    return nothing;
  }
  const cpu = snap.host.cpuPercent;
  const mem = snap.host.memUsedPercent;
  const harness = snap.harness;
  const execution = harness.inference.execution.replace(/_/g, " ");
  const throughput = harness.lastInference?.tokensPerSec;

  const cards = [
    {
      label: "CPU",
      value: `${cpu}%`,
      sub: `${snap.host.cpuCount} cores`,
      spark: renderSparkline(props.history, "cpuPercent"),
    },
    {
      label: "Memory",
      value: `${mem}%`,
      sub: "host usage",
      spark: renderSparkline(props.history, "memUsedPercent"),
    },
    {
      label: "Ollama lanes",
      value: `${harness.harness.ollamaInflightRuns}/${snap.ollama.ollamaNumParallel ?? "?"}`,
      sub: "inflight / max",
      spark: nothing,
    },
    {
      label: "Gateway lane",
      value: `${snap.gateway.activeRuns}/${snap.gateway.mainMaxConcurrent}`,
      sub: `${snap.gateway.mainSlotsFree} slots free`,
      spark: nothing,
    },
    {
      label: "Inference",
      value: execution,
      sub: harness.inference.loadedVramBytes ? "VRAM reported" : "no VRAM signal",
      spark: nothing,
    },
    {
      label: "Probe / throughput",
      value: `${snap.ollama.probeMs ?? "?"} ms`,
      sub: throughput != null ? `${throughput.toFixed(2)} tok/s` : "throughput n/a",
      spark: nothing,
    },
  ];

  return html`
    <div class="local-llm-bottleneck-strip" role="list" aria-label="Local LLM bottleneck indicators">
      ${cards.map(
        (card) => html`
          <div class="local-llm-bottleneck" role="listitem">
            <div class="local-llm-bottleneck__label">${card.label}</div>
            <div class="local-llm-bottleneck__value">${card.value}</div>
            <div class="local-llm-bottleneck__sub muted">${card.sub}</div>
            ${card.spark}
          </div>
        `,
      )}
    </div>
  `;
}
